import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiUser, FiBriefcase, FiAlertCircle } from 'react-icons/fi';
import { useProveedor } from '../../hooks/useProveedores/useProveedor';
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';
import MultiPhoneInput from '../../components/Forms/MultiPhoneInput';

const ProveedorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    proveedor,
    isEditing,
    isLoading,
    error,
    hasChanges,
    updateProveedorData,
    saveProveedor,
    resetProveedor,
    clearError
  } = useProveedor(id);

  const [validationErrors, setValidationErrors] = useState({});
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingZipCode, setIsLoadingZipCode] = useState(false);

  const fieldLabels = {
    name: 'Nombre de la Compañía',
    address: 'Dirección',
    unit: 'Unidad',
    city: 'Ciudad',
    state: 'Estado',
    postal_code: 'Código Postal',
    country: 'País',
    phone: 'Teléfono',
    phone_secondary: 'Teléfono Secundario',
    email: 'Email',
    website: 'Página Web',
    contact_name: 'Nombre de Contacto',
    credit_days: 'Días de Crédito',
    credit_limit: 'Límite de Crédito',
    payment_method: 'Método de Pago',
    payment_terms: 'Términos de Pago',
    bank_name: 'Nombre del Banco',
    account_number: 'Número de Cuenta',
    routing_number: 'Número de Ruta',
    swift_code: 'Código SWIFT',
    notes: 'Notas',
    status: 'Estado',
  };

  const translateError = (message) => {
    if (!message) return message;
    return Object.entries(fieldLabels).reduce((msg, [eng, esp]) => {
      return msg.replace(new RegExp(`\\b${eng}\\b`, 'gi'), esp);
    }, message);
  };

  // Opciones para los selectores
  const paises = [
    { value: 'Estados Unidos', label: 'Estados Unidos' },
    { value: 'Canadá', label: 'Canadá' },
    { value: 'Puerto Rico', label: 'Puerto Rico' }
  ];

  const estados = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' }
  ];

  // Función para buscar información por código postal
  const handleZipCodeLookup = async (zipCode) => {
    if (!zipCode || zipCode.length < 5) return;

    setIsLoadingZipCode(true);

    try {
      // API gratuita de Zippopotam para USA
      const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);

      if (response.ok) {
        const data = await response.json();

        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          updateProveedorData('city', place['place name']);
          updateProveedorData('state', place['state abbreviation']);
          updateProveedorData('country', 'Estados Unidos');
        }
      }
    } catch (error) {
      console.error('Error al buscar código postal:', error);
    } finally {
      setIsLoadingZipCode(false);
    }
  };

  // Limpiar errores cuando se monta el componente
  useEffect(() => {
    clearError();
    setValidationErrors({});
  }, [clearError]);

  const handleInputChange = (field, value) => {
    updateProveedorData(field, value);
    
    
    // Limpiar error de validación del campo específico
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSaving(true);
    setValidationErrors({});
    
    try {
      const result = await saveProveedor();

      if (result.success) {
        // Navegar de vuelta a la lista con mensaje de éxito
        navigate('/system/proveedores', {
          state: {
            message: result.message,
            type: 'success'
          }
        });
      } else {
        // Procesar errores de validación
        const processedErrors = {};
        
        // Si hay validation_errors (array format)
        if (result.validation_errors && Array.isArray(result.validation_errors)) {
          result.validation_errors.forEach(err => {
            processedErrors[err.field] = translateError(err.message);
          });
        }

        // Si hay validationErrors (object format)
        if (result.validationErrors && typeof result.validationErrors === 'object') {
          const translated = {};
          Object.entries(result.validationErrors).forEach(([field, message]) => {
            translated[field] = translateError(message);
          });
          Object.assign(processedErrors, translated);
        }
        
        // Si no hay errores específicos pero hay error general
        if (Object.keys(processedErrors).length === 0 && result.error) {
          processedErrors.general = result.error;
        }
        
        setValidationErrors(processedErrors);
      }
    } catch (err) {
      setValidationErrors({ 
        general: 'Error inesperado al guardar el proveedor' 
      });
      console.error('Unexpected error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      navigate('/system/proveedores');
    }
  };

  const confirmCancel = () => {
    resetProveedor();
    navigate('/system/proveedores');
  };

  if (isLoading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner-single" style={{ width: '40px', height: '40px' }}></div>
        <div className="loading-message">
          {isEditing ? 'Cargando proveedor...' : 'Preparando formulario...'}
        </div>
      </div>
    );
  }

  return (
    <div className="cliente-form-page">
      {/* Header */}
      <div className="form-header">
        <button 
          className="btn btn--ghost"
          onClick={handleCancel}
          type="button"
        >
          <FiArrowLeft /> Volver a Proveedores
        </button>
        
        <h1 className="form-title">
          {isEditing ? 'EDITAR PROVEEDOR' : 'NUEVO PROVEEDOR'}
        </h1>
        
        <div className="form-actions">
          <button 
            className="btn btn--outline"
            onClick={handleCancel}
            type="button"
            disabled={isSaving}
          >
            <FiX /> Cancelar
          </button>
          
          <button 
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isSaving || !hasChanges}
            type="submit"
          >
            {isSaving ? (
              <>
                <div className="loading-spinner-single" style={{ width: '16px', height: '16px', marginRight: '8px' }}></div>
                Guardando...
              </>
            ) : (
              <>
                <FiSave /> {isEditing ? 'Actualizar' : 'Crear'} Proveedor
              </>
            )}
          </button>
        </div>
      </div>

      {/* Errores generales */}
      {(error || validationErrors.general) && (
        <div className="alert alert--danger">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error || validationErrors.general}
          </div>
        </div>
      )}

      {/* Errores de validación específicos */}
      {Object.keys(validationErrors).length > 1 || (Object.keys(validationErrors).length === 1 && !validationErrors.general) ? (
        <div className="alert alert--warning">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Errores de validación:</strong>
            <ul className="error-list">
              {Object.entries(validationErrors)
                .filter(([field]) => field !== 'general')
                .map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))
              }
            </ul>
          </div>
        </div>
      ) : null}

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="cliente-form">
        <div className="form-columns">
          {/* Columna Izquierda */}
          <div className="form-column">
            {/* Sección: Información de la Compañía */}
            <div className="form-section">
              <div className="section-header">
                <FiBriefcase className="section-icon" />
                <h2 className="section-title">Información de la Compañía</h2>
              </div>
              
              <div className="form-group">
                <label className="form-label required">Nombre de la Compañía</label>
                <input
                  type="text"
                  className={`form-input ${validationErrors.name ? 'error' : ''}`}
                  value={proveedor.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Ingrese el nombre de la compañía"
                  maxLength={255}
                />
                {validationErrors.name && (
                  <span className="form-error">{validationErrors.name}</span>
                )}
              </div>
              
              <div className="form-group">
                <label className="form-label required">Dirección</label>
                <input
                  type="text"
                  className={`form-input ${validationErrors.address ? 'error' : ''}`}
                  value={proveedor.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Número y calle"
                />
                {validationErrors.address && (
                  <span className="form-error">{validationErrors.address}</span>
                )}
              </div>
<div className="form-group">                <label className="form-label">Unidad</label>                <input                  type="text"                  className="form-input"                  value={proveedor.unit || ''}                  onChange={(e) => handleInputChange('unit', e.target.value)}                  placeholder="Apartamento, suite, unidad"                  maxLength={50}                />              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Ciudad</label>
                  <input
                    type="text"
                    className="form-input"
                    value={proveedor.city || ''}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="Ciudad"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <select
                    className="form-input"
                    value={proveedor.state || ''}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                  >
                    <option value="">Seleccione estado</option>
                    {estados.map(estado => (
                      <option key={estado.value} value={estado.value}>{estado.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">
                    Código Postal
                    {isLoadingZipCode && <span style={{ marginLeft: '8px', fontSize: '0.85em', color: '#666' }}>(Buscando...)</span>}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={proveedor.postal_code || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange('postal_code', value);

                      // Buscar automáticamente cuando tenga 5 dígitos
                      if (value && value.length === 5) {
                        handleZipCodeLookup(value);
                      }
                    }}
                    placeholder="Código postal"
                    maxLength="5"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">País</label>
                  <select
                    className={`form-input ${validationErrors.country ? 'error' : ''}`}
                    value={proveedor.country || ''}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                  >
                    <option value="">Seleccione país</option>
                    {paises.map(pais => (
                      <option key={pais.value} value={pais.value}>{pais.label}</option>
                    ))}
                  </select>
                  {validationErrors.country && (
                    <span className="form-error">{validationErrors.country}</span>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Página Web</label>
                <input
                  type="url"
                  className="form-input"
                  value={proveedor.website || ''}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="https://www.ejemplo.com"
                />
              </div>
            </div>


          </div>

          {/* Columna Derecha */}
          <div className="form-column">
            {/* Sección: Información de Contacto */}
            <div className="form-section">
              <div className="section-header">
                <FiUser className="section-icon" />
                <h2 className="section-title">Contacto</h2>
              </div>
              
              <div className="form-group">
                <label className="form-label">Nombre del Contacto</label>
                <input
                  type="text"
                  className="form-input"
                  value={proveedor.contact_name || ''}
                  onChange={(e) => handleInputChange('contact_name', e.target.value)}
                  placeholder="Nombre de la persona de contacto"
                  maxLength={255}
                />
              </div>

              <MultiPhoneInput
                phones={proveedor.phones || []}
                onChange={(phones) => handleInputChange('phones', phones)}
                label="Teléfono"
                required={true}
                error={validationErrors.phone}
              />

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className={`form-input ${validationErrors.email ? 'error' : ''}`}
                  value={proveedor.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="correo@ejemplo.com"
                />
                {validationErrors.email && (
                  <span className="form-error">{validationErrors.email}</span>
                )}
              </div>
            {/* Sección: Términos de Pago */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Términos de Pago</h3>
              </div>
              
              <div className="form-group">
                <label className="form-label">Términos de Pago (Días)</label>
                <input
                  type="number"
                  className="form-input"
                  value={proveedor.credit_days || ''}
                  onChange={(e) => handleInputChange('credit_days', parseInt(e.target.value) || 0)}
                  placeholder="30"
                  min="0"
                  max="365"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Cantidad de Crédito (%)</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    className="form-input"
                    value={proveedor.credit_limit || ''}
                    onChange={(e) => handleInputChange('credit_limit', parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.01"
                    style={{ paddingRight: '35px' }}
                  />
                  <span style={{ 
                    position: 'absolute', 
                    right: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6c757d',
                    pointerEvents: 'none',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>%</span>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Tipo de Pago</label>
                <select
                  className="form-input"
                  value={proveedor.payment_method || 'COD'}
                  onChange={(e) => handleInputChange('payment_method', e.target.value)}
                >
                  <option value="COD">COD</option>
                  <option value="CHECK">CHEQUE</option>
                  <option value="BANK_TRANSFER">TRANSFERENCIA</option>
                  <option value="ZELLE">ZELLE</option>
                </select>
              </div>
            </div>
            </div>


          </div>
        </div>
      </form>

      {/* Modal de confirmación para cambios no guardados */}
      <ConfirmModal
        isOpen={showUnsavedChangesModal}
        onClose={() => setShowUnsavedChangesModal(false)}
        onConfirm={confirmCancel}
        title="¿Descartar cambios?"
        message="Tienes cambios sin guardar. ¿Estás seguro de que quieres salir sin guardar?"
        confirmText="Sí, descartar"
        cancelText="Continuar editando"
        type="warning"
      />
    </div>
  );
};

export default ProveedorForm;